import { Vector2 } from 'three';

import type { CutoutManager } from '../cutout-manager.class';
import type { CutoutInfo } from '../types';
import type { CutoutInfo as LegacyCutoutInfo } from '../../../../model/cutout-info';
import { ventOpeningProfiles } from '../helpers/vent-openings.helpers';

export interface AddLegacyCutoutParams {
	cutout: LegacyCutoutInfo;
}

/**
 * Add a cutout from legacy format (from model/cutout-info.ts)
 */
export const addLegacyCutoutCfn = (
	self: CutoutManager,
	{ cutout }: AddLegacyCutoutParams
): void => {
	// An arch is a vertical profile (width x height), not a footprint, so it replaces the
	// vertices outright and is extruded horizontally into the panel.
	if (cutout.archProfile && cutout.width > 0 && cutout.height > 0) {
		const halfWidth = cutout.width / 2;
		const spring = cutout.height * 0.78;
		const rise = cutout.height - spring;
		const radius = ((halfWidth * halfWidth) + (rise * rise)) / (2 * rise);
		const centreY = cutout.height - radius;
		const arch: Vector2[] = [new Vector2(-halfWidth, 0), new Vector2(-halfWidth, spring)];
		const a0 = Math.atan2(spring - centreY, -halfWidth);
		const a1 = Math.atan2(spring - centreY, halfWidth);
		for (let i = 1; i < 24; i++) {
			const a = a0 + (((a1 - a0) * i) / 24);
			arch.push(new Vector2(Math.cos(a) * radius, centreY + (Math.sin(a) * radius)));
		}
		arch.push(new Vector2(halfWidth, spring), new Vector2(halfWidth, 0));
		self.addCutout({
			id: `cutout-${cutout.islandIndex}-${self.cutouts.length}`,
			islandPositionIndex: cutout.islandIndex,
			islandId: cutout.islandId,
			points: arch,
			depth: cutout.depth,
			position: { x: cutout.x, y: cutout.y, z: cutout.z },
			rotation: cutout.rotationY ? { x: 0, y: cutout.rotationY, z: 0 } : undefined,
			applianceId: cutout.applianceId,
			verticalProfile: true
		});
		return;
	}

	// A vent cuts the slots of its 2D artwork, not its bounding box, so it replaces the vertices
	// with one profile per slot and extrudes them horizontally through the panel. Surround only --
	// a top vent sits against the underside of the slab and must not cut the worktop.
	if (cutout.ventProfile && cutout.width > 0 && cutout.height > 0) {
		const shapes = ventOpeningProfiles(
			cutout.ventProfile.svgKey, cutout.width, cutout.height, cutout.ventProfile.panelWidth
		);
		if (!shapes.length) return;

		self.addCutout({
			id: `cutout-${cutout.islandIndex}-${self.cutouts.length}`,
			islandPositionIndex: cutout.islandIndex,
			islandId: cutout.islandId,
			points: shapes[0],
			shapes,
			depth: cutout.depth,
			position: { x: cutout.x, y: cutout.y, z: cutout.z },
			rotation: cutout.rotationY ? { x: 0, y: cutout.rotationY, z: 0 } : undefined,
			applianceId: cutout.applianceId,
			verticalProfile: true,
			surroundOnly: true
		});
		return;
	}

	// Convert legacy cutout vertices to Vector2 points
	const points: Vector2[] = cutout.vertices.map((v) => new Vector2(v.x, v.y));

	// If no vertices but we have dimensions, create a rectangular cutout
	// The cutout shape is defined in 2D (width x depth), extruded by height
	if (points.length === 0 && cutout.width > 0 && cutout.depth > 0) {
		const halfWidth = cutout.width / 2;
		const halfDepth = cutout.depth / 2;
		points.push(
			new Vector2(-halfWidth, -halfDepth),
			new Vector2(halfWidth, -halfDepth),
			new Vector2(halfWidth, halfDepth),
			new Vector2(-halfWidth, halfDepth)
		);
	}

	if (points.length < 3) {
		console.warn(`CutoutManager: Cutout has less than 3 points (${points.length}), skipping. ` +
			`width=${cutout.width}, height=${cutout.height}, depth=${cutout.depth}, vertices=${cutout.vertices.length}`);
		return;
	}

	const newCutout: CutoutInfo = {
		id: `cutout-${cutout.islandIndex}-${self.cutouts.length}`,
		islandPositionIndex: cutout.islandIndex,
		islandId: cutout.islandId,
		points,
		depth: cutout.height, // height is the extrusion depth (vertical)
		position: {
			x: cutout.x,
			y: cutout.y,
			z: cutout.z
		},
		rotation: cutout.rotationY ? { x: 0, y: cutout.rotationY, z: 0 } : undefined,
		applianceId: cutout.applianceId
	};

	self.addCutout(newCutout);
};
